import { AxiosError } from 'axios'
import { useMutation, type UseMutationOptions } from 'react-query'

import agent from 'agent'
import useAuth from 'hooks/use-auth'
import { getEndpointWithParams } from 'queries/queries-utils'
import { statusChecksMutationKeys } from 'queries/status-checks/query-keys'

import type {
  DynatraceStatusCheckCreateRequest,
  StatusCheck
} from 'types/api/status-checks/status-check'

function updateDynatraceStatusCheck(
  mutationKey:
    | ReturnType<typeof statusChecksMutationKeys.mutateDynatraceStatusCheck>
    | undefined
) {
  return async function (params: DynatraceStatusCheckCreateRequest) {
    if (!mutationKey) {
      throw new AxiosError(
        'Cannot update a Dynatrace status check without a valid status check ID'
      )
    }

    const endpointWithParams = getEndpointWithParams(...mutationKey)
    const result = await agent.put<StatusCheck>(endpointWithParams, params)

    return result.data
  }
}

export function useUpdateDynatraceStatusCheck(
  statusCheckId: string | undefined,
  options?: Omit<
    UseMutationOptions<
      StatusCheck,
      AxiosError,
      DynatraceStatusCheckCreateRequest
    >,
    'mutationKey' | 'mutationFn'
  >
) {
  const { currentTeamId: teamId } = useAuth()
  const mutationKey = statusCheckId
    ? statusChecksMutationKeys.mutateDynatraceStatusCheck({
        statusCheckId,
        teamId
      })
    : undefined

  const mutation = useMutation({
    ...options,
    mutationKey,
    mutationFn: updateDynatraceStatusCheck(mutationKey)
  })

  return mutation
}
